import * as yup from "yup";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { CgAdd, CgCloseO } from "react-icons/cg";

import { TreeNode } from "./AccountPlanTree";
import useDeleteFinancialCategory from "features/condominio/hooks/useDeleteFinancialCategory";
import { useForm } from "react-hook-form";
import useInsertFinancialCategory from "features/condominio/hooks/useInsertFinancialCategory";
import useUpdateFinancialCategory from "features/condominio/hooks/useUpdateFinancialCategory";
import { yupResolver } from "@hookform/resolvers/yup";
import colors from "core/resources/theme/colors";
import { showError, showConfirm, showSuccess } from "infra/helpers/alerta";
import { useInvalidateQuery } from "infra/packages/react-query";
import { useDetalhesHeader } from "components/Layout/Header/hooks/useDetalhesHeader";

interface NodeProps {
  parent?: TreeNode | undefined;
  node: TreeNode;
  edit?: boolean;
  tenant?: string;
}

interface FormFields {
  nome: string;
}

export default function Node({ parent, tenant, node, edit }: NodeProps) {
  const { register, handleSubmit, formState, watch } = useForm<FormFields>({
    defaultValues: {
      nome: node.nome,
    },
    mode: "onBlur",
    shouldFocusError: false,
    resolver: yupResolver(scheme),
  });
  const { mutate: updateMutate, isLoading: isUpdating } =
    useUpdateFinancialCategory(tenant);
  const { mutate: insertMutate } = useInsertFinancialCategory();
  const { mutate: deleteMutate, isLoading: isDeleting } =
    useDeleteFinancialCategory();
  const { data: passosCadastro } = useDetalhesHeader();
  const { doInvalidateQueryWithStringKey } = useInvalidateQuery();

  const onSubmit = (data: any) => {
    const updated = {
      ...node,
      ...data,
      categoriaFinanceiraPaiId: parent?.id,
    };
    if (!isUpdating || !isDeleting) {
      updateMutate(updated, {
        onSuccess: () => {
          doInvalidateQueryWithStringKey("/v1/categoria-financeira");
          if (
            data &&
            passosCadastro?.some((passo) => passo.porcentagemConcluido < 100)
          ) {
            doInvalidateQueryWithStringKey("/v1/tenants/passos-cadastro");
          }
        },
      });
    }
  };

  return (
    <Box title={`${node.id || node.key} | ${node.nome}`}>
      {edit ? (
        <Flex mb={4}>
          <Box
            as="form"
            w="100%"
            mr={2}
            onBlur={handleSubmit(onSubmit)}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Flex>
              <Flex pt="10px" px="8px">
                {node.breadcrumb ?? ""}
              </Flex>
              <FormControl
                isDisabled={!!node.readOnly}
                id="nome"
                isInvalid={!!formState.errors?.nome}
              >
                <InputGroup>
                  {!node.readOnly ? <Input {...register("nome")} /> : null}
                  {node.readOnly ? (
                    <Text pt="10px" fontWeight="bold">
                      {watch("nome")}
                    </Text>
                  ) : null}
                  {isUpdating && (
                    <InputRightElement>
                      <Spinner size="xs" />
                    </InputRightElement>
                  )}
                </InputGroup>
                <FormErrorMessage mt={0}>
                  {formState.errors?.nome?.message}
                </FormErrorMessage>
              </FormControl>
            </Flex>
          </Box>
          {edit && (
            <>
              <Button
                variant="ghost"
                mr={node.readOnly ? "57px" : undefined}
                onClick={() => {
                  if (parent?.items?.length) {
                    if (!parent.items[0].nome) {
                      return showError("Registro anterior inválido!");
                    }
                  }

                  if (node.items?.length) {
                    if (!node.items[0].nome) {
                      return showError("Registro anterior inválido!");
                    }
                  }

                  insertMutate({ ...node } as any);
                }}
              >
                <Icon as={CgAdd} w={8} h={8} color={colors.primary} />
              </Button>
              {!node.readOnly ? (
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (node.items?.length) {
                      return showError(
                        "Não é possível excluir esta categoria. Exclua os itens filhos e tente novamente!"
                      );
                    }
                    showConfirm("Deseja realmente excluir a categoria?").then(
                      ({ isConfirmed }) => {
                        if (isConfirmed) {
                          deleteMutate(node as any, {
                            onSuccess: () => {
                              showSuccess();
                            },
                          });
                        }
                      }
                    );
                  }}
                  isLoading={isDeleting}
                >
                  {!isDeleting ? (
                    <Icon as={CgCloseO} w={8} h={8} color={colors.red} />
                  ) : null}
                </Button>
              ) : null}
            </>
          )}
        </Flex>
      ) : (
        <Text pb={4}>
          {node.breadcrumb} - {node.nome}
        </Text>
      )}
    </Box>
  );
}

const scheme = yup.object().shape({
  nome: yup.string().required("Este campo não pode ser vazio."),
});
